import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '../lib/cx';
import { IconButton } from './Button';

function useModal(open: boolean, onClose: () => void) {
  const panel = useRef<HTMLDivElement>(null);
  // Latest onClose without re-running the effect: callers pass inline functions, and
  // re-running on every render would pull focus out of inputs inside the dialog.
  const close = useRef(onClose);
  close.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close.current();
      if (e.key === 'Tab' && panel.current) {
        // Keep focus inside the dialog.
        const f = panel.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (f.length === 0) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    setTimeout(() => panel.current?.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
      prev?.focus?.();
    };
  }, [open]);
  return panel;
}

/**
 * Bottom sheet on phones (thumb-reachable), centred panel on desktop.
 * Used for help text, option hints, pickers and quick actions.
 */
export function Sheet({
  open,
  onClose,
  title,
  children,
  footer,
  closeLabel = 'Close',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  closeLabel?: string;
}) {
  const panel = useModal(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="animate-fade-in absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className="animate-sheet-up relative flex max-h-[88vh] w-full flex-col rounded-t-2xl border border-border bg-surface shadow-elevated outline-none sm:max-w-lg sm:rounded-2xl"
      >
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-border-strong sm:hidden" aria-hidden="true" />
        <div className="flex items-center justify-between gap-3 border-b border-border/75 px-5 py-3.5">
          <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
          <IconButton icon="x" label={closeLabel} onClick={onClose} size="sm" />
        </div>
        <div className="overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="pb-safe flex gap-2 border-t border-border bg-surface-2/40 px-5 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}

/** Confirmation / small form dialog. */
export function Dialog({
  open,
  onClose,
  title,
  children,
  actions,
  tone = 'default',
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children?: ReactNode;
  actions: ReactNode;
  tone?: 'default' | 'danger';
}) {
  const panel = useModal(open, onClose);
  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="animate-fade-in absolute inset-0 bg-black/50 backdrop-blur-[2px]" onClick={onClose} aria-hidden="true" />
      <div
        ref={panel}
        role="alertdialog"
        aria-modal="true"
        tabIndex={-1}
        className={cx(
          'animate-fade-in relative w-full max-w-md rounded-2xl border border-border bg-surface p-6 shadow-elevated outline-none',
          tone === 'danger' && 'border-t-4 border-t-danger',
        )}
      >
        <h2 className="font-display text-lg font-bold tracking-tight">{title}</h2>
        {children && <div className="mt-2 text-sm text-muted">{children}</div>}
        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{actions}</div>
      </div>
    </div>,
    document.body,
  );
}
